"use client";

import { X } from "lucide-react";

/**
 * Chip — toggleable filter pill, accent-aware via --cc-accent-* CSS vars.
 *
 * The active color follows whatever accent the user has selected in Settings
 * (default: teal). This means a single Chip component themes itself across
 * the whole app without consumers caring about the current palette.
 *
 * Variants:
 *  - "filter" (default): rounded-full, used in chip rows for selecting tags
 *  - "compact": same shape, smaller padding/text — for dense rows
 *
 * Pass `prefix` for code-style mini-labels (e.g. "100" before "בנייה").
 */
// Omit the native HTML `prefix` attribute (which is `string | undefined`) so we
// can repurpose it for ReactNode without a type conflict.
export interface ChipProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "prefix"> {
  active: boolean;
  variant?: "filter" | "compact";
  prefix?: React.ReactNode;
  hideCloseIcon?: boolean;
}

export function Chip({
  active,
  variant = "filter",
  prefix,
  hideCloseIcon,
  className = "",
  children,
  ...rest
}: ChipProps) {
  const base =
    variant === "compact"
      ? "rounded-full border px-2.5 py-0.5 text-[11px] cursor-pointer flex items-center gap-1 transition"
      : "rounded-full border px-3 py-1 text-xs cursor-pointer flex items-center gap-1 transition";

  // Active state uses --cc-accent-500 (semi-transparent fill + border).
  // GAM Brand v2: adopt the active skin's spring curve for transitions.
  // Chips stay pills (rounded-full) regardless of skin.
  const activeStyle: React.CSSProperties = active
    ? {
        backgroundColor: "var(--cc-accent-500-15)",
        borderColor: "var(--cc-accent-500-50)",
        color: "var(--cc-accent-300)",
        transitionTimingFunction: "var(--gam-spring-default, cubic-bezier(0.4, 0, 0.2, 1))",
      }
    : {
        transitionTimingFunction: "var(--gam-spring-default, cubic-bezier(0.4, 0, 0.2, 1))",
      };

  const inactive =
    "border-slate-700/50 bg-slate-800/40 text-slate-400 hover:border-slate-600 hover:text-slate-200 hover:bg-slate-800/70";

  return (
    <button
      type="button"
      style={activeStyle}
      className={`${base} ${active ? "" : inactive} ${className}`}
      {...rest}
    >
      {active && !hideCloseIcon && <X className="h-3 w-3" />}
      {prefix && <span className="opacity-50 text-[10px] font-mono">{prefix}</span>}
      {children}
    </button>
  );
}
